import { useState, useEffect, useRef } from "react";
import { SidebarProvider, SidebarInset, SidebarTrigger } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/AppSidebar";
import { normalizeModule } from "@/lib/moduleContext";
import { moduleThemeClass } from "@/lib/modules/moduleThemeClass";

import { EnemSidebar } from "@/components/EnemSidebar";
import { OabSidebar } from "@/components/OabSidebar";
import { VestibularesSidebar } from "@/components/VestibularesSidebar";
import { VideoaulasSidebar } from "@/components/VideoaulasSidebar";
import { TribunaisSidebar } from "@/components/TribunaisSidebar";
import { PoliciaisSidebar } from "@/components/PoliciaisSidebar";
import { JuridicasSidebar } from "@/components/JuridicasSidebar";
import { CnuSidebar } from "@/components/CnuSidebar";
import { BancarioSidebar } from "@/components/BancarioSidebar";
import { MilitaresSidebar } from "@/components/MilitaresSidebar";
import { ResidenciaSidebar } from "@/components/ResidenciaSidebar";
import { ColegiosSidebar } from "@/components/ColegiosSidebar";
import { Separator } from "@/components/ui/separator";
import { Breadcrumb, BreadcrumbItem, BreadcrumbList, BreadcrumbPage } from "@/components/ui/breadcrumb";
import { useLocation, useNavigate } from "react-router-dom";
import { ThemeToggle } from "@/components/ThemeToggle";
import { NotificationCenter } from "@/components/NotificationCenter";
import { HeaderProgressIndicator } from "@/components/HeaderProgressIndicator";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { MobileBottomNav } from "@/components/MobileBottomNav";

import { SystemRatingModal } from "@/components/SystemRatingModal";
import { UpdateAvailableToast } from "@/components/UpdateAvailableToast";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Settings, LogOut, Flame, BookOpen, GraduationCap, Scale, PlayCircle, BookMarked, Building2, Landmark, Receipt } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useProfile } from "@/hooks/useProfile";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import brandLogoHorizontal from "@/assets/brand/nivela-logo-horizontal.png";
import brandLogoHorizontalDarkAsset from "@/assets/brand/nivela-logo-horizontal-dark.png.asset.json";
const brandLogoHorizontalDark = brandLogoHorizontalDarkAsset.url;

const LEVEL_NAMES = ['Iniciante', 'Estudante', 'Dedicado', 'Avançado', 'Expert', 'Mestre', 'Lenda'];

interface AppLayoutProps {
  children: React.ReactNode;
  title?: string;
}

const routeTitles: Record<string, string> = {
  '/dashboard': 'Dashboard',
  '/questions': 'Questões',
  '/simulados': 'Simulados',
  '/flashcards': 'Flashcards',
  '/pomodoro': 'Pomodoro',
  '/analytics': 'Analytics',
  '/ranking': 'Ranking',
  '/history': 'Histórico',
  '/challenges': 'Desafios',
  '/groups': 'Grupos de Estudo',
  '/smart-review': 'Meu Caderno',
  '/discipline-goals': 'Metas por Disciplina',
  '/achievements': 'Conquistas',
  '/enem/achievements': 'Conquistas ENEM',
  '/profile': 'Perfil',
  '/admin': 'Painel Administrativo',
  '/enem': 'Hub ENEM',
  '/enem/questions': 'Questões ENEM',
  '/enem/simulations': 'Simulados ENEM',
  '/enem/simulados/criar': 'Criar Simulado ENEM',
  '/enem/full-simulation': 'Simulado Completo',
  '/enem/smart-review': 'Revisão Espaçada',
  '/enem/weak-areas': 'Áreas Fracas',
  '/enem/errors': 'Padrões de Erro',
  '/enem/result': 'Resultado do Simulado',
  '/enem/review': 'Revisão de Questões',
  '/enem/settings': 'Configurações ENEM',
  '/enem/ranking': 'Ranking ENEM',
  '/enem/pomodoro': 'Pomodoro ENEM',
  '/enem/redacao': 'Redação ENEM',
  '/oab/pomodoro': 'Pomodoro OAB',
  '/oab': 'Dashboard OAB',
  '/oab/questions': 'Questões OAB',
  '/oab/simulations': 'Simulados OAB',
  '/oab/simulations/create': 'Criar Simulado OAB',
  '/oab/ranking': 'Ranking OAB',
  '/oab/settings': 'Configurações OAB',
  '/vestibulares': 'Dashboard Vestibulares',
  '/vestibulares/questions': 'Questões de Vestibulares',
  '/vestibulares/simulations': 'Simulados de Vestibulares',
  '/vestibulares/simulations/create': 'Criar Simulado de Vestibular',
  '/vestibulares/ranking': 'Ranking Vestibulares',
  '/vestibulares/analytics': 'Desempenho Vestibulares',
  '/vestibulares/settings': 'Configurações Vestibulares',
  '/vestibulares/pomodoro': 'Pomodoro Vestibulares',
  '/tribunais': 'Dashboard Tribunais',
  '/tribunais/questions': 'Questões de Tribunais',
  '/tribunais/simulations': 'Simulados de Tribunais',
  '/tribunais/ranking': 'Ranking Tribunais',
  '/tribunais/analytics': 'Desempenho Tribunais',
  '/tribunais/settings': 'Configurações Tribunais',
  '/tribunais/smart-review': 'Meu Caderno Tribunais',
  '/tribunais/error-patterns': 'Padrões de Erro Tribunais',
  '/tribunais/weak-areas': 'Áreas Fracas Tribunais',
  '/tribunais/achievements': 'Conquistas Tribunais',
  '/tribunais/pomodoro': 'Pomodoro Tribunais',
  '/carreiras-policiais': 'Dashboard Carreiras Policiais',
  '/carreiras-policiais/questions': 'Questões de Carreiras Policiais',
  '/carreiras-policiais/simulations': 'Simulados de Carreiras Policiais',
  '/carreiras-policiais/ranking': 'Ranking Carreiras Policiais',
  '/carreiras-policiais/analytics': 'Desempenho Carreiras Policiais',
  '/carreiras-policiais/settings': 'Configurações Carreiras Policiais',
  '/carreiras-policiais/smart-review': 'Meu Caderno Carreiras Policiais',
  '/carreiras-policiais/error-patterns': 'Padrões de Erro Carreiras Policiais',
  '/carreiras-policiais/weak-areas': 'Áreas Fracas Carreiras Policiais',
  '/carreiras-policiais/achievements': 'Conquistas Carreiras Policiais',
  '/carreiras-policiais/pomodoro': 'Pomodoro Carreiras Policiais',
  '/carreiras-juridicas': 'Dashboard Carreiras Jurídicas',
  '/carreiras-juridicas/questions': 'Questões de Carreiras Jurídicas',
  '/carreiras-juridicas/simulations': 'Simulados de Carreiras Jurídicas',
  '/carreiras-juridicas/ranking': 'Ranking Carreiras Jurídicas',
  '/carreiras-juridicas/analytics': 'Desempenho Carreiras Jurídicas',
  '/carreiras-juridicas/settings': 'Configurações Carreiras Jurídicas',
  '/carreiras-juridicas/smart-review': 'Meu Caderno Carreiras Jurídicas',
  '/carreiras-juridicas/error-patterns': 'Padrões de Erro Carreiras Jurídicas',
  '/carreiras-juridicas/weak-areas': 'Áreas Fracas Carreiras Jurídicas',
  '/carreiras-juridicas/achievements': 'Conquistas Carreiras Jurídicas',
  '/carreiras-juridicas/pomodoro': 'Pomodoro Carreiras Jurídicas',
  '/cnu': 'Dashboard CNU',
  '/cnu/questions': 'Questões do CNU',
  '/cnu/simulations': 'Simulados do CNU',
  '/cnu/ranking': 'Ranking CNU',
  '/cnu/analytics': 'Desempenho CNU',
  '/cnu/settings': 'Configurações CNU',
  '/cnu/smart-review': 'Meu Caderno CNU',
  '/cnu/error-patterns': 'Padrões de Erro CNU',
  '/cnu/weak-areas': 'Áreas Fracas CNU',
  '/cnu/achievements': 'Conquistas CNU',
  '/cnu/pomodoro': 'Pomodoro CNU',
  '/bancario': 'Dashboard Bancário',
  '/bancario/questions': 'Questões Bancárias',
  '/bancario/simulations': 'Simulados Bancários',
  '/bancario/ranking': 'Ranking Bancário',
  '/bancario/analytics': 'Desempenho Bancário',
  '/bancario/settings': 'Configurações Bancário',
  '/bancario/smart-review': 'Meu Caderno Bancário',
  '/bancario/error-patterns': 'Padrões de Erro Bancário',
  '/bancario/weak-areas': 'Áreas Fracas Bancário',
  '/bancario/achievements': 'Conquistas Bancário',
  '/bancario/pomodoro': 'Pomodoro Bancário',
  '/militares': 'Dashboard Militares',
  '/militares/questions': 'Questões Militares',
  '/militares/simulations': 'Simulados Militares',
  '/militares/ranking': 'Ranking Militares',
  '/militares/analytics': 'Desempenho Militares',
  '/militares/settings': 'Configurações Militares',
  '/militares/smart-review': 'Meu Caderno Militares',
  '/militares/error-patterns': 'Padrões de Erro Militares',
  '/militares/weak-areas': 'Áreas Fracas Militares',
  '/militares/achievements': 'Conquistas Militares',
  '/militares/pomodoro': 'Pomodoro Militares',
  '/residencia': 'Dashboard Residência Médica',
  '/residencia/questions': 'Questões Residência Médica',
  '/residencia/simulations': 'Simulados Residência Médica',
  '/residencia/ranking': 'Ranking Residência Médica',
  '/residencia/analytics': 'Desempenho Residência Médica',
  '/residencia/settings': 'Configurações Residência Médica',
  '/residencia/smart-review': 'Meu Caderno Residência',
  '/residencia/error-patterns': 'Padrões de Erro Residência',
  '/residencia/weak-areas': 'Áreas Fracas Residência',
  '/residencia/achievements': 'Conquistas Residência',
  '/residencia/pomodoro': 'Pomodoro Residência',
  '/colegios': 'Dashboard Colégios Militares & Técnicos',
  '/colegios/questions': 'Questões Colégios',
  '/colegios/simulations': 'Simulados Colégios',
  '/colegios/ranking': 'Ranking Colégios',
  '/colegios/analytics': 'Desempenho Colégios',
  '/colegios/settings': 'Configurações Colégios',
  '/colegios/smart-review': 'Meu Caderno Colégios',
  '/colegios/error-patterns': 'Padrões de Erro Colégios',
  '/colegios/weak-areas': 'Áreas Fracas Colégios',
  '/colegios/achievements': 'Conquistas Colégios',
  '/colegios/pomodoro': 'Pomodoro Colégios',
  '/settings/concursos': 'Configurações Concursos',
  '/concursos/autoavaliacao': 'Autoavaliação do Concurseiro',
  '/enem/autoavaliacao': 'Autoavaliação do Vestibulando',
  '/oab/autoavaliacao': 'Autoavaliação do Examinando OAB',
  '/simulados/criar': 'Criar Simulado',
  '/admin/analytics': 'Analytics',
  '/admin/questions': 'Questões Concursos',
  '/admin/enem-questions': 'Questões ENEM',
  '/admin/simulations': 'Simulados',
  '/admin/flashcards': 'Flashcards',
  '/admin/flashcard-generator': 'Gerador Flashcards IA',
  '/admin/disciplines': 'Disciplinas',
  '/admin/exam-boards': 'Bancas',
  '/admin/users': 'Usuários',
  '/admin/pci-import': 'Importar PCI',
  '/admin/enem-import': 'Importar ENEM',
  '/admin/subject-linking': 'Vincular Matérias',
  '/admin/discipline-stats': 'Estatísticas Disciplinas',
  '/admin/oab-import': 'Importar OAB',
  '/admin/courses': 'Gerenciar Cursos',
  '/admin/teachers': 'Professores',
  '/videoaulas': 'Videoaulas',
};

const moduleConfig = {
  concursos: {
    label: "Concursos",
    icon: BookOpen,
    badgeClass: "bg-emerald-600 text-white border-0",
    headerBg: "bg-background/95",
    borderColor: "border-b",
    titleColor: "text-foreground",
  },
  enem: {
    label: "ENEM",
    icon: GraduationCap,
    badgeClass: "bg-gradient-to-r from-blue-600 to-purple-600 text-white border-0",
    headerBg: "bg-gradient-to-r from-blue-100/80 to-purple-100/80 dark:from-blue-950/50 dark:to-purple-950/50",
    borderColor: "border-b border-blue-500/50",
    titleColor: "text-blue-700 dark:text-blue-300",
  },
  oab: {
    label: "OAB",
    icon: Scale,
    badgeClass: "bg-gradient-to-r from-amber-600 to-amber-800 text-white border-0",
    headerBg: "bg-gradient-to-r from-amber-100/80 to-orange-100/80 dark:from-amber-950/50 dark:to-orange-950/50",
    borderColor: "border-b border-amber-500/50",
    titleColor: "text-amber-700 dark:text-amber-300",
  },
  vestibulares: {
    label: "Vestibulares",
    icon: BookMarked,
    badgeClass: "bg-gradient-to-r from-indigo-600 to-indigo-800 text-white border-0",
    headerBg: "bg-gradient-to-r from-indigo-100/80 to-violet-100/80 dark:from-indigo-950/50 dark:to-violet-950/50",
    borderColor: "border-b border-indigo-500/50",
    titleColor: "text-indigo-700 dark:text-indigo-300",
  },
  tribunais: {
    label: "Tribunais",
    icon: Building2,
    badgeClass: "bg-gradient-to-r from-indigo-700 to-blue-800 text-white border-0",
    headerBg: "bg-gradient-to-r from-indigo-100/80 to-blue-100/80 dark:from-indigo-950/50 dark:to-blue-950/50",
    borderColor: "border-b border-indigo-600/50",
    titleColor: "text-indigo-800 dark:text-indigo-300",
  },
  "carreiras-policiais": {
    label: "Carreiras Policiais",
    icon: Building2,
    badgeClass: "bg-gradient-to-r from-blue-700 to-blue-900 text-white border-0",
    headerBg: "bg-gradient-to-r from-blue-100/80 to-sky-100/80 dark:from-blue-950/50 dark:to-sky-950/50",
    borderColor: "border-b border-blue-700/50",
    titleColor: "text-blue-800 dark:text-blue-300",
  },
  "carreiras-juridicas": {
    label: "Carreiras Jurídicas",
    icon: Scale,
    badgeClass: "bg-gradient-to-r from-[#7C1D2E] to-[#5a1422] text-white border-0",
    headerBg: "bg-gradient-to-r from-rose-100/80 to-red-100/80 dark:from-rose-950/50 dark:to-red-950/50",
    borderColor: "border-b border-[#7C1D2E]/50",
    titleColor: "text-[#7C1D2E] dark:text-rose-300",
  },
  cnu: {
    label: "CNU",
    icon: Building2,
    badgeClass: "bg-gradient-to-r from-[#00853F] to-[#00692f] text-white border-0",
    headerBg: "bg-gradient-to-r from-emerald-100/80 to-green-100/80 dark:from-emerald-950/50 dark:to-green-950/50",
    borderColor: "border-b border-[#00853F]/50",
    titleColor: "text-[#00853F] dark:text-emerald-300",
  },
  bancario: {
    label: "Bancário",
    icon: Landmark,
    badgeClass: "bg-gradient-to-r from-[#FFD500] to-[#FFC000] text-[#0A1F3F] border-0",
    headerBg: "bg-gradient-to-r from-yellow-100/80 to-amber-100/80 dark:from-yellow-950/40 dark:to-blue-950/50",
    borderColor: "border-b border-[#FFD500]/60",
    titleColor: "text-[#003D7C] dark:text-yellow-300",
  },
  militares: {
    label: "Militares",
    icon: Building2,
    badgeClass: "bg-gradient-to-r from-[#4B5320] to-[#3a4019] text-[#F5F1E6] border-0",
    headerBg: "bg-gradient-to-r from-[#4B5320]/10 to-[#C9B037]/10 dark:from-[#4B5320]/30 dark:to-[#C9B037]/20",
    borderColor: "border-b border-[#4B5320]/60",
    titleColor: "text-[#4B5320] dark:text-[#C9B037]",
  },
  residencia: {
    label: "Residência Médica",
    icon: Building2,
    badgeClass: "bg-gradient-to-r from-[#0E7C5A] to-[#0A5A42] text-white border-0",
    headerBg: "bg-gradient-to-r from-emerald-100/80 to-green-100/80 dark:from-emerald-950/40 dark:to-green-950/40",
    borderColor: "border-b border-[#0E7C5A]/60",
    titleColor: "text-[#0E7C5A] dark:text-emerald-300",
  },
  colegios: {
    label: "Colégios Militares & Técnicos",
    icon: Building2,
    badgeClass: "bg-gradient-to-r from-[#3D5240] to-[#2a3a2d] text-[#F5F1E6] border-0",
    headerBg: "bg-gradient-to-r from-[#3D5240]/10 to-[#B8924A]/10 dark:from-[#3D5240]/30 dark:to-[#B8924A]/20",
    borderColor: "border-b border-[#3D5240]/60",
    titleColor: "text-[#3D5240] dark:text-[#B8924A]",
  },
  videoaulas: {
    label: "Videoaulas",
    icon: PlayCircle,
    badgeClass: "bg-gradient-to-r from-purple-600 to-pink-600 text-white border-0",
    headerBg: "bg-gradient-to-r from-purple-100/80 to-pink-100/80 dark:from-purple-950/50 dark:to-pink-950/50",
    borderColor: "border-b border-purple-500/50",
    titleColor: "text-purple-700 dark:text-purple-300",
  },
};

const neutralRoutes = ['/profile', '/achievements', '/ranking', '/challenges', '/groups', '/pomodoro', '/pomodoro-history', '/plans'];

export function AppLayout({ children, title }: AppLayoutProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const pageTitle = title || routeTitles[location.pathname] || 'Nível A Educação';
  
  // Determine module from route, falling back to localStorage for neutral routes
  const isNeutralRoute = neutralRoutes.some(r => location.pathname === r || location.pathname.startsWith(r + '/'));
  const concursosRoutes = ['/dashboard', '/questions', '/simulados', '/flashcards', 
    '/analytics', '/ranking', '/history', '/smart-review', '/discipline-goals',
    '/favorites', '/favorites-study', '/settings/concursos', '/study-stats',
    '/question', '/concursos/autoavaliacao', '/concursos/study-plan'];

  const routeModule = location.pathname.startsWith('/videoaulas') ? 'videoaulas'
    : location.pathname.startsWith('/vestibulares') ? 'vestibulares'
    : location.pathname.startsWith('/carreiras-policiais') ? 'carreiras-policiais'
    : location.pathname.startsWith('/carreiras-juridicas') ? 'carreiras-juridicas'
    : location.pathname.startsWith('/cnu') ? 'cnu'
    : location.pathname.startsWith('/bancario') ? 'bancario'
    : location.pathname.startsWith('/militares') ? 'militares'
    : location.pathname.startsWith('/residencia') ? 'residencia'
    : location.pathname.startsWith('/colegios') ? 'colegios'
    : location.pathname.startsWith('/tribunais') ? 'tribunais'
    : location.pathname.startsWith('/oab') ? 'oab'
    : location.pathname.startsWith('/enem') ? 'enem'
    : location.pathname.startsWith('/admin') ? 'concursos'
    : concursosRoutes.some(r => location.pathname === r || location.pathname.startsWith(r + '/'))
      ? 'concursos'
    : null;

  const currentModule = routeModule ?? (
    (typeof window !== 'undefined' ? localStorage.getItem('last_active_module') : null) as 'concursos' | 'enem' | 'oab' | 'videoaulas' | 'vestibulares' | 'tribunais' | 'carreiras-policiais' | 'carreiras-juridicas' | 'cnu' | 'bancario' | 'militares' | 'residencia' | 'colegios' | null
  ) ?? 'concursos';

  // Save module to localStorage when on a module-specific route
  useEffect(() => {
    if (routeModule) {
      localStorage.setItem('last_active_module', routeModule);
    }
  }, [routeModule]);

  const isEnemModule = currentModule === 'enem';
  const isOabModule = currentModule === 'oab';
  const isVestibularesModule = currentModule === 'vestibulares';
  const isVideoaulasModule = currentModule === 'videoaulas';
  const isTribunaisModule = currentModule === 'tribunais';
  const isPoliciaisModule = currentModule === 'carreiras-policiais';
  const isJuridicasModule = currentModule === 'carreiras-juridicas';
  const isCnuModule = currentModule === 'cnu';
  const isBancarioModule = currentModule === 'bancario';
  const isMilitaresModule = currentModule === 'militares';
  const isResidenciaModule = currentModule === 'residencia';
  const isColegiosModule = currentModule === 'colegios';
  const config = moduleConfig[currentModule] || moduleConfig.concursos;
  const ModuleIcon = config.icon;
  const [isModuleTransitioning, setIsModuleTransitioning] = useState(false);
  const previousModuleRef = useRef(currentModule);
  useEffect(() => {
    if (previousModuleRef.current !== currentModule) {
      setIsModuleTransitioning(true);
      const timer = setTimeout(() => {
        setIsModuleTransitioning(false);
      }, 400);
      previousModuleRef.current = currentModule;
      return () => clearTimeout(timer);
    }
  }, [currentModule]);

  const { profile, userInitials } = useProfile();

  const { data: userStats } = useQuery({
    queryKey: ['header-stats', user?.id],
    queryFn: async () => {
      if (!user) return null;
      const [streakRes, pointsRes] = await Promise.all([
        supabase.from('user_streaks').select('current_streak').eq('user_id', user.id).maybeSingle(),
        supabase.from('user_points').select('current_level').eq('user_id', user.id).eq('module_type', 'concursos').order('current_level', { ascending: false }).limit(1).maybeSingle(),
      ]);
      return {
        streak: streakRes.data?.current_streak || 0,
        level: pointsRes.data?.current_level || 1,
      };
    },
    enabled: !!user,
  });


  const handleSignOut = async () => {
    try { await supabase.auth.signOut(); } catch (err) { console.warn('signOut failed', err); }
    navigate('/');
  };

  return (
    <SidebarProvider>
      <div className={cn("h-screen flex w-full overflow-hidden", moduleThemeClass(currentModule))}>
        {isVideoaulasModule ? <VideoaulasSidebar /> : isColegiosModule ? <ColegiosSidebar /> : isResidenciaModule ? <ResidenciaSidebar /> : isMilitaresModule ? <MilitaresSidebar /> : isBancarioModule ? <BancarioSidebar /> : isCnuModule ? <CnuSidebar /> : isJuridicasModule ? <JuridicasSidebar /> : isPoliciaisModule ? <PoliciaisSidebar /> : isTribunaisModule ? <TribunaisSidebar /> : isVestibularesModule ? <VestibularesSidebar /> : isOabModule ? <OabSidebar /> : isEnemModule ? <EnemSidebar /> : <AppSidebar />}
        <SidebarInset className="flex-1 min-w-0 overflow-hidden flex flex-col h-full">
          <header className={cn(
            "sticky top-0 z-50 flex h-14 shrink-0 items-center justify-between gap-2 px-4",
            "backdrop-blur supports-[backdrop-filter]:bg-background/60",
            "transition-all duration-300 ease-out",
            config.headerBg,
            config.borderColor
          )}>
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <SidebarTrigger className="-ml-1 hidden md:flex flex-shrink-0" />
              <Separator orientation="vertical" className="mr-2 h-4 hidden md:block" />
              <Badge className={cn("text-xs hidden md:inline-flex flex-shrink-0", config.badgeClass)}>
                <ModuleIcon className="h-3 w-3 mr-1" />
                {config.label}
              </Badge>
              <Separator orientation="vertical" className="mx-2 h-4 hidden md:block" />
              <img src={brandLogoHorizontal} alt="Nivela" className="h-7 w-auto md:hidden block dark:hidden" />
              <img src={brandLogoHorizontalDark} alt="Nivela" className="h-7 w-auto md:hidden hidden dark:block" />
              <Breadcrumb className="min-w-0 hidden md:block">
                <BreadcrumbList>
                  <BreadcrumbItem className="min-w-0">
                    <BreadcrumbPage className={cn("truncate", config.titleColor)}>{pageTitle}</BreadcrumbPage>
                  </BreadcrumbItem>
                </BreadcrumbList>
              </Breadcrumb>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <Separator orientation="vertical" className="h-4 hidden sm:block" />
              <div className="hidden sm:block">
                <HeaderProgressIndicator moduleType={normalizeModule(currentModule)} />
              </div>
              {userStats && (
                <div className="flex items-center gap-2">
                  <Separator orientation="vertical" className="h-4 hidden sm:block" />
                  <div className="hidden sm:flex items-center gap-1 px-2 py-1 rounded-md bg-orange-500/10 text-orange-500">
                    <Flame className="h-3.5 w-3.5" />
                    <span className="text-sm font-bold">{userStats.streak}</span>
                  </div>
                  <Badge variant="secondary" className="text-xs hidden md:inline-flex">
                    {LEVEL_NAMES[Math.min(userStats.level - 1, LEVEL_NAMES.length - 1)]}
                  </Badge>
                </div>
              )}
              <NotificationCenter />
              <ThemeToggle />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center rounded-full hover:ring-2 hover:ring-primary/20 transition-all">
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={profile?.avatar_url || undefined} />
                      <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                        {userInitials}
                      </AvatarFallback>
                    </Avatar>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuItem onClick={() => navigate('/profile')}>
                    <Settings className="mr-2 h-4 w-4" />
                    Perfil
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/conta/assinaturas')}>
                    <Receipt className="mr-2 h-4 w-4" />
                    Minha assinatura
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleSignOut} className="text-destructive">
                    <LogOut className="mr-2 h-4 w-4" />
                    Sair
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </header>
          <main className="flex-1 overflow-auto min-h-0 scrollbar-hide p-4 md:p-6 pb-20 md:pb-6">
            <div className={cn(
              "transition-all duration-300",
              isModuleTransitioning ? "animate-module-switch" : "animate-page-enter"
            )}>
              {children}
            </div>
          </main>
          <MobileBottomNav />
        </SidebarInset>
      </div>
      <SystemRatingModal />
      <UpdateAvailableToast />
    </SidebarProvider>
  );
}
